/**
 * FO-144 (batch-28) RI&E — gedeelde types, labels, scoring-helpers en
 * Zod-request-schema's (client-side én server-side, conform de
 * validatie-standaard: één bron, fouten inline via issuesToFieldErrors).
 *
 * Scoring (Fine & Kinney): score = kans × effect × blootstelling (elk een
 * integer 1-5). Prioriteit: 1-9 laag / 10-39 middel / 40-125 hoog. De server
 * is leidend; de UI rekent live mee met exact dezelfde helpers hieronder.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums (string-unions, parallel aan Prisma) ──────────────────────────────

export const RIE_STATUS = ['CONCEPT', 'TER_TOETSING', 'VASTGESTELD'] as const
export type RieStatus = (typeof RIE_STATUS)[number]

export const RIE_PRIORITEIT = ['LAAG', 'MIDDEL', 'HOOG'] as const
export type RiePrioriteit = (typeof RIE_PRIORITEIT)[number]

export const RIE_MAATREGEL_TYPE = ['BRON', 'COLLECTIEF', 'INDIVIDUEEL', 'PBM'] as const
export type RieMaatregelType = (typeof RIE_MAATREGEL_TYPE)[number]

export const RIE_MAATREGEL_STATUS = ['OPEN', 'LOPEND', 'AFGEROND', 'VERVALLEN'] as const
export type RieMaatregelStatus = (typeof RIE_MAATREGEL_STATUS)[number]

export const RIE_ANTWOORD = ['JA', 'NEE', 'NVT'] as const
export type RieAntwoord = (typeof RIE_ANTWOORD)[number]

// ─── Labels + badge-varianten ────────────────────────────────────────────────

export const RIE_STATUS_LABELS: Record<RieStatus, string> = {
  CONCEPT: 'Concept',
  TER_TOETSING: 'Ter toetsing',
  VASTGESTELD: 'Vastgesteld',
}
export const RIE_STATUS_BADGE: Record<RieStatus, 'info' | 'warning' | 'success'> = {
  CONCEPT: 'info',
  TER_TOETSING: 'warning',
  VASTGESTELD: 'success',
}

export const RIE_PRIORITEIT_LABELS: Record<RiePrioriteit, string> = {
  LAAG: 'Laag',
  MIDDEL: 'Middel',
  HOOG: 'Hoog',
}
export const RIE_PRIORITEIT_BADGE: Record<RiePrioriteit, 'success' | 'warning' | 'danger'> = {
  LAAG: 'success',
  MIDDEL: 'warning',
  HOOG: 'danger',
}

export const RIE_MAATREGEL_TYPE_LABELS: Record<RieMaatregelType, string> = {
  BRON: 'Bronmaatregel',
  COLLECTIEF: 'Collectieve maatregel',
  INDIVIDUEEL: 'Individuele maatregel',
  PBM: 'PBM',
}

export const RIE_MAATREGEL_STATUS_LABELS: Record<RieMaatregelStatus, string> = {
  OPEN: 'Open',
  LOPEND: 'Lopend',
  AFGEROND: 'Afgerond',
  VERVALLEN: 'Vervallen',
}
export const RIE_MAATREGEL_STATUS_BADGE: Record<
  RieMaatregelStatus,
  'info' | 'warning' | 'success' | 'secondary'
> = {
  OPEN: 'info',
  LOPEND: 'warning',
  AFGEROND: 'success',
  VERVALLEN: 'secondary',
}

export const RIE_ANTWOORD_LABELS: Record<RieAntwoord, string> = {
  JA: 'Ja',
  NEE: 'Nee',
  NVT: 'N.v.t.',
}

// ─── Wegingstabel (scores 1-5 met labels, sectie 3 van de RI&E) ──────────────

export interface RieWegingRij {
  score: 1 | 2 | 3 | 4 | 5
  kans: string
  effect: string
  blootstelling: string
}

export const RIE_WEGING_TABEL: RieWegingRij[] = [
  { score: 1, kans: 'Zeer onwaarschijnlijk', effect: 'Gering letsel, geen verzuim (EHBO)', blootstelling: 'Zelden (enkele keren per jaar)' },
  { score: 2, kans: 'Onwaarschijnlijk', effect: 'Letsel met kort verzuim', blootstelling: 'Soms (maandelijks)' },
  { score: 3, kans: 'Mogelijk', effect: 'Ernstig letsel met verzuim', blootstelling: 'Regelmatig (wekelijks)' },
  { score: 4, kans: 'Waarschijnlijk', effect: 'Zeer ernstig of blijvend letsel', blootstelling: 'Vaak (dagelijks)' },
  { score: 5, kans: 'Zeer waarschijnlijk', effect: 'Dodelijk letsel', blootstelling: 'Voortdurend (hele dienst)' },
]

/**
 * Vaste weergavevolgorde van de 16 FO302-thema's (seed-volgorde). Onbekende
 * (later door HR toegevoegde) thema-keys sorteren erna, alfabetisch.
 */
export const RIE_THEMA_VOLGORDE: readonly string[] = [
  'arbozorg',
  'bhv',
  'werkplaats',
  'kantoor',
  'klantlocaties',
  'machines',
  'elektrisch',
  'gereedschap',
  'hoogte',
  'fysieke-belasting',
  'gevaarlijke-stoffen',
  'omgevingsfactoren',
  'pbm',
  'verkeer',
  'psa',
  'bijzondere-groepen',
] as const

/** Sorteerwaarde voor een themaKey (seed-volgorde; onbekend = achteraan). */
export function rieThemaSortIndex(themaKey: string): number {
  const idx = RIE_THEMA_VOLGORDE.indexOf(themaKey)
  return idx === -1 ? RIE_THEMA_VOLGORDE.length : idx
}

// ─── Scoring-helpers (server leidend; UI rekent live mee) ────────────────────

/** Prioriteit-grenzen (inclusief): 1-9 laag / 10-39 middel / 40-125 hoog. */
export const RIE_PRIORITEIT_GRENZEN = {
  LAAG: { min: 1, max: 9 },
  MIDDEL: { min: 10, max: 39 },
  HOOG: { min: 40, max: 125 },
} as const

/** True als de waarde een geldige wegingsscore is (integer 1-5). */
export function isGeldigeWegingScore(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 5
}

/**
 * Risicoscore = kans × effect × blootstelling. Geeft `null` zodra een van de
 * drie ontbreekt of ongeldig is — nooit 0 of een negatief getal (FO-144 §Tests).
 */
export function berekenRieScore(
  kans: number | null | undefined,
  effect: number | null | undefined,
  blootstelling: number | null | undefined,
): number | null {
  if (!isGeldigeWegingScore(kans) || !isGeldigeWegingScore(effect) || !isGeldigeWegingScore(blootstelling)) {
    return null
  }
  return kans * effect * blootstelling
}

/** Prioriteit uit een score (1-125); `null` in → `null` uit. */
export function riePrioriteitVanScore(score: number | null | undefined): RiePrioriteit | null {
  if (typeof score !== 'number' || !Number.isInteger(score) || score < 1) return null
  if (score <= RIE_PRIORITEIT_GRENZEN.LAAG.max) return 'LAAG'
  if (score <= RIE_PRIORITEIT_GRENZEN.MIDDEL.max) return 'MIDDEL'
  return 'HOOG'
}

// ─── Zod-schema's (gedeeld: API .parse/.safeParse + client-side form) ────────

/** Datum zonder tijd (yyyy-MM-dd) — read-DTO's geven datums date-only terug. */
const dateOnly = (label: string) =>
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: `${label} moet een datum (jjjj-mm-dd) zijn` })

const wegingScore = (label: string) =>
  z
    .number({ message: `${label} moet een getal zijn` })
    .int({ message: `${label} moet een geheel getal zijn` })
    .min(1, { message: `${label} moet tussen 1 en 5 liggen` })
    .max(5, { message: `${label} moet tussen 1 en 5 liggen` })

export const createRieBeoordelingSchema = z.object({
  entityId: idParam('entityId'),
  vestigingLocatie: z.string().trim().max(200).nullable().optional(),
})
export type CreateRieBeoordelingInput = z.infer<typeof createRieBeoordelingSchema>

/**
 * Alle bewerkbare velden van een RI&E in CONCEPT/TER_TOETSING. Elke waarde die
 * de read-DTO als `null` teruggeeft is hier `.nullable()` (read↔write
 * consistent). Score-/statusvelden ontbreken bewust: die zijn server-side.
 */
export const updateRieBeoordelingSchema = z.object({
  vestigingLocatie: z.string().trim().max(200).nullable().optional(),
  scopeRie: z.string().trim().max(5000).nullable().optional(),
  uitgeslotenOnderdelen: z.string().trim().max(5000).nullable().optional(),
  aantalMedewerkers: z.number().int().min(0).max(100000).nullable().optional(),
  // Fte met 2 decimalen; als string of number geaccepteerd (Decimal-veld).
  aantalFte: z
    .union([z.number().min(0).max(100000), z.string().regex(/^\d+([.,]\d{1,2})?$/)])
    .nullable()
    .optional(),
  preventiemedewerkerId: idParam('preventiemedewerkerId').nullable().optional(),
  arbodienst: z.string().trim().max(200).nullable().optional(),
  bhvVerantwoordelijkeId: idParam('bhvVerantwoordelijkeId').nullable().optional(),
  medewerkersBetrokken: z.string().trim().max(5000).nullable().optional(),
  datumRondgang: dateOnly('Datum rondgang').nullable().optional(),
  geraadpleegdeDocumenten: z.string().trim().max(5000).nullable().optional(),
  aantalOngevallen12m: z.number().int().min(0).max(100000).nullable().optional(),
  aantalBijnaOngevallen12m: z.number().int().min(0).max(100000).nullable().optional(),
  verzuimpercentage: z
    .union([z.number().min(0).max(100), z.string().regex(/^\d+([.,]\d{1,2})?$/)])
    .nullable()
    .optional(),
  incidentenToelichting: z.string().trim().max(5000).nullable().optional(),
  verbeterpunten: z.string().trim().max(5000).nullable().optional(),
  toetsingNodig: z.boolean().nullable().optional(),
  toetsingsroute: z.string().trim().max(300).nullable().optional(),
  getoetstDoor: z.string().trim().max(200).nullable().optional(),
  datumToetsing: dateOnly('Datum toetsing').nullable().optional(),
  redenToetsingskeuze: z.string().trim().max(2000).nullable().optional(),
  opgesteldDoorId: idParam('opgesteldDoorId').nullable().optional(),
  volgendeHerziening: dateOnly('Volgende herziening').nullable().optional(),
})
export type UpdateRieBeoordelingInput = z.infer<typeof updateRieBeoordelingSchema>

/** Risicoregister-regel (create + update). Score/prioriteit zijn server-side. */
export const upsertRieRisicoSchema = z.object({
  themaKey: z.string().trim().max(100).nullable().optional(),
  locatieFunctie: z.string().trim().max(300).nullable().optional(),
  gevaar: z.string().trim().min(1, { message: 'Omschrijf het gevaar of risico' }).max(2000),
  bestaandeMaatregelen: z.string().trim().max(2000).nullable().optional(),
  kans: wegingScore('Kans').nullable().optional(),
  effect: wegingScore('Effect').nullable().optional(),
  blootstelling: wegingScore('Blootstelling').nullable().optional(),
  volgorde: z.number().int().min(0).optional(),
})
export type UpsertRieRisicoInput = z.infer<typeof upsertRieRisicoSchema>

/** Plan-van-aanpak-maatregel (create + update in CONCEPT/TER_TOETSING). */
export const upsertRieMaatregelSchema = z.object({
  risicoId: idParam('risicoId').nullable().optional(),
  omschrijving: z.string().trim().min(1, { message: 'Omschrijf de maatregel' }).max(2000),
  type: z.enum(RIE_MAATREGEL_TYPE),
  actiehouderId: idParam('actiehouderId').nullable().optional(),
  deadline: dateOnly('Deadline').nullable().optional(),
  status: z.enum(RIE_MAATREGEL_STATUS).optional(),
  controleEffect: z.string().trim().max(2000).nullable().optional(),
  volgorde: z.number().int().min(0).optional(),
})
export type UpsertRieMaatregelInput = z.infer<typeof upsertRieMaatregelSchema>

/** Alleen de status van een maatregel — het enige muteerbare ná vaststelling. */
export const updateRieMaatregelStatusSchema = z.object({
  status: z.enum(RIE_MAATREGEL_STATUS),
})
export type UpdateRieMaatregelStatusInput = z.infer<typeof updateRieMaatregelStatusSchema>

/**
 * Thema-antwoord (upsert per vraag). Regel (FO-144 §6): bij NEE is een
 * gekoppeld risico óf een toelichting verplicht — afgedwongen in dit gedeelde
 * schema zodat het form dezelfde fout inline toont als de API teruggeeft.
 */
export const upsertRieThemaAntwoordSchema = z
  .object({
    vraagId: idParam('vraagId'),
    antwoord: z.enum(RIE_ANTWOORD),
    toelichting: z.string().trim().max(2000).nullable().optional(),
    bewijsDocumentId: idParam('bewijsDocumentId').nullable().optional(),
    gekoppeldRisicoId: idParam('gekoppeldRisicoId').nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.antwoord === 'NEE' && !val.gekoppeldRisicoId && !val.toelichting?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toelichting'],
        message: 'Bij "Nee" is een gekoppeld risico of een toelichting verplicht',
      })
    }
  })
export type UpsertRieThemaAntwoordInput = z.infer<typeof upsertRieThemaAntwoordSchema>

/** Ter toetsing aanbieden (optionele toetsing-metadata in één stap). */
export const rieTerToetsingSchema = z.object({
  toetsingsroute: z.string().trim().max(300).nullable().optional(),
  getoetstDoor: z.string().trim().max(200).nullable().optional(),
})
export type RieTerToetsingInput = z.infer<typeof rieTerToetsingSchema>

/**
 * Vaststellen (digitale ondertekening door de directie/eindverantwoordelijke).
 * `volgendeHerziening` is verplicht (voedt het herzieningssignaal);
 * `redenToetsingskeuze` is verplicht wanneer TER_TOETSING wordt overgeslagen
 * (direct vanaf CONCEPT vaststellen, FO-144 §11) — de API dwingt dat af omdat
 * alleen de server de huidige status kent.
 */
export const rieVaststellenSchema = z.object({
  vastgesteldDoorId: idParam('vastgesteldDoorId'),
  vastgesteldFunctie: z.string().trim().max(200).nullable().optional(),
  // Base64 PNG data-URL van het handtekening-canvas.
  handtekeningData: z
    .string()
    .min(1, { message: 'Handtekening is verplicht' })
    .regex(/^data:image\/png;base64,/, { message: 'Handtekening moet een PNG-handtekening zijn' })
    .max(2_000_000, { message: 'Handtekening is te groot' }),
  volgendeHerziening: dateOnly('Volgende herziening'),
  redenToetsingskeuze: z.string().trim().max(2000).nullable().optional(),
})
export type RieVaststellenInput = z.infer<typeof rieVaststellenSchema>

// ─── DTO's (API read-shapes) ─────────────────────────────────────────────────

export interface RieThemaVraagDTO {
  id: string
  themaKey: string
  themaTitel: string
  vraag: string
  volgorde: number
  actief: boolean
}

export interface RieRisicoDTO {
  id: string
  themaKey: string | null
  locatieFunctie: string | null
  gevaar: string
  bestaandeMaatregelen: string | null
  kans: number | null
  effect: number | null
  blootstelling: number | null
  score: number | null
  prioriteit: RiePrioriteit | null
  volgorde: number
  /** Aantal gekoppelde (niet-vervallen) maatregelen — voor de hoog-risico-check in de UI. */
  aantalMaatregelen: number
}

export interface RieMaatregelDTO {
  id: string
  risicoId: string | null
  risicoGevaar: string | null
  omschrijving: string
  type: RieMaatregelType
  actiehouderId: string | null
  actiehouderNaam: string | null
  deadline: string | null
  status: RieMaatregelStatus
  controleEffect: string | null
  volgorde: number
}

export interface RieThemaAntwoordDTO {
  id: string
  vraagId: string
  antwoord: RieAntwoord
  toelichting: string | null
  bewijsDocumentId: string | null
  bewijsDocumentTitel: string | null
  gekoppeldRisicoId: string | null
}

export interface RieBeoordelingListRowDTO {
  id: string
  entityId: string
  entityNaam: string
  vestigingLocatie: string | null
  versie: number
  status: RieStatus
  registernummer: string | null
  /** Voortgang plan van aanpak: afgeronde / totaal (VERVALLEN telt niet mee). */
  maatregelenAfgerond: number
  maatregelenTotaal: number
  volgendeHerziening: string | null
  vastgesteldOp: string | null
  /** True zolang er geen nieuwere versie bestaat (opvolger == null). */
  actueel: boolean
  updatedAt: string
}

export interface RieBeoordelingDetailDTO {
  id: string
  entityId: string
  entityNaam: string
  vestigingLocatie: string | null
  versie: number
  status: RieStatus
  scopeRie: string | null
  uitgeslotenOnderdelen: string | null
  aantalMedewerkers: number | null
  aantalFte: string | null
  preventiemedewerkerId: string | null
  preventiemedewerkerNaam: string | null
  arbodienst: string | null
  bhvVerantwoordelijkeId: string | null
  bhvVerantwoordelijkeNaam: string | null
  medewerkersBetrokken: string | null
  datumRondgang: string | null
  geraadpleegdeDocumenten: string | null
  aantalOngevallen12m: number | null
  aantalBijnaOngevallen12m: number | null
  verzuimpercentage: string | null
  incidentenToelichting: string | null
  verbeterpunten: string | null
  toetsingNodig: boolean | null
  toetsingsroute: string | null
  getoetstDoor: string | null
  datumToetsing: string | null
  redenToetsingskeuze: string | null
  opgesteldDoorId: string | null
  opgesteldDoorNaam: string | null
  vastgesteldDoorId: string | null
  vastgesteldDoorNaam: string | null
  vastgesteldFunctie: string | null
  vastgesteldOp: string | null
  terToetsingOp: string | null
  volgendeHerziening: string | null
  registernummer: string | null
  vorigeVersieId: string | null
  opvolgerId: string | null
  pdfDocumentId: string | null
  /** Vergrendeld = VASTGESTELD: alleen maatregel-statussen nog muteerbaar. */
  vergrendeld: boolean
  risicos: RieRisicoDTO[]
  maatregelen: RieMaatregelDTO[]
  themaAntwoorden: RieThemaAntwoordDTO[]
  createdAt: string
  updatedAt: string
}

/** Blokkade-informatie voor ter-toetsing/vaststellen (hoog risico zonder maatregel). */
export interface RieBlokkadeDTO {
  /** Risico's met prioriteit HOOG zonder gekoppelde (niet-vervallen) maatregel. */
  hoogZonderMaatregel: Array<{ id: string; gevaar: string; score: number | null }>
}
